<script setup>
import { computed, useSlots } from "vue";
import NextButton from "./NextButton.vue";
import BackButton from "./BackButton.vue";

const props = defineProps(["step", "processing"]);

const slots = useSlots();

const nextDisplayable = computed(() => {
    if (props.step.next === undefined) {
        return false;
    }

    if (props.step.next.display !== undefined) {
        if (typeof props.step.next.display === "function") {
            return props.step.next.display();
        } else {
            return props.step.next.display;
        }
    }

    return true;
});

const nextDisabled = computed(() => {
    if (!nextDisplayable.value) {
        return true;
    }

    if (props.processing) {
        return true;
    }

    if (props.step.next.disabled !== undefined) {
        if (typeof props.step.next.disabled === "function") {
            return props.step.next.disabled();
        } else {
            return props.step.next.disabled;
        }
    }

    return false;
});

const nextLabel = computed(() => {
    if (!nextDisplayable.value) {
        return null;
    }

    if (props.step.next.label !== undefined) {
        if (typeof props.step.next.label === "function") {
            return props.step.next.label();
        } else {
            return props.step.next.label;
        }
    }
});

const nextShowIcon = computed(() => {
    if (!nextDisplayable.value) {
        return false;
    }

    if (props.step.next.showIcon !== undefined) {
        if (typeof props.step.next.showIcon === "function") {
            return props.step.next.showIcon();
        } else {
            return props.step.next.showIcon;
        }
    }

    return true;
});

const nextIcon = computed(() => {
    if (!nextDisplayable.value) {
        return false;
    }

    if (props.step.next.nextIcon !== undefined) {
        if (typeof props.step.next.nextIcon === "function") {
            return props.step.next.nextIcon();
        } else {
            return props.step.next.nextIcon;
        }
    }

    return undefined;
});

const backDisplayable = computed(() => {
    if (props.step.back === undefined) {
        return false;
    }

    if (props.step.back.display !== undefined) {
        if (typeof props.step.back.display === "function") {
            return props.step.back.display();
        } else {
            return props.step.back.display;
        }
    }

    return true;
});

const backDisabled = computed(() => {
    if (!backDisplayable.value) {
        return true;
    }

    if (props.processing) {
        return true;
    }

    if (props.step.back.disabled !== undefined) {
        if (typeof props.step.back.disabled === "function") {
            return props.step.back.disabled();
        } else {
            return props.step.back.disabled;
        }
    }

    return false;
});

const backLabel = computed(() => {
    if (!backDisplayable.value) {
        return null;
    }

    if (props.step.back.label !== undefined) {
        if (typeof props.step.back.label === "function") {
            return props.step.back.label();
        } else {
            return props.step.back.label;
        }
    }
});

const skipDisplayable = computed(() => {
    if (props.step.skip === undefined) {
        return false;
    }

    if (props.step.skip.display !== undefined) {
        if (typeof props.step.skip.display === "function") {
            return props.step.skip.display();
        } else {
            return props.step.skip.display;
        }
    }

    return true;
});

const skipLabel = computed(() => {
    if (!skipDisplayable.value) {
        return null;
    }

    if (props.step.skip.label !== undefined) {
        if (typeof props.step.skip.label === "function") {
            return props.step.skip.label();
        } else {
            return props.step.skip.label;
        }
    }

    return "Skip";
});
</script>
<template>
    <div>
        <div v-if="!!slots.header" class="py-6">
            <slot name="header"></slot>
        </div>
        <div v-if="!!slots.default" class="py-6">
            <slot name="default"></slot>
        </div>
        <div v-if="!!slots.footer || nextDisplayable || skipDisplayable || backDisplayable">
            <template v-if="!!slots.footer">
                <slot name="footer"></slot>
            </template>
            <template v-else>
                <div class="flex flex-col justify-center items-center py-6 space-y-3">
                    <NextButton
                        v-if="nextDisplayable"
                        @click="$emit('next')"
                        :disabled="nextDisabled"
                        :label="nextLabel"
                        :show-icon="nextShowIcon"
                        :icon="nextIcon" />
                    <div
                        v-if="skipDisplayable"
                        @click="$emit('skip')"
                        class="text-sm text-gray-400 cursor-pointer dark:hover:text-gray-200 hover:text-gray-600 hover:underline">
                        {{ skipLabel }}
                    </div>
                    <BackButton v-if="backDisplayable" @click="$emit('back')" :disabled="backDisabled" :label="backLabel" />
                </div>
            </template>
        </div>
    </div>
</template>
